import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../../components/common/Logo";
import { authService } from "../../lib/api/auth";
import { useTranslation } from "react-i18next";

type PageState = 'form' | 'success';

export const ForgotPasswordPage = () => {
    const { t } = useTranslation("common");
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pageState, setPageState] = useState<PageState>('form');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await authService.forgotPassword(email);
            setPageState('success');
        } catch (err: any) {
            const message = err.response?.data?.error || err.message || 'Failed to send reset link. Please try again.';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background-page flex flex-col">
            {/* Navigation */}
            <nav className="container-custom py-4 px-4 sm:py-6 sm:px-6">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <Logo className="w-6 h-6" />
                        <span className="text-lg sm:text-2xl font-medium font-display text-text-primary">
                            Dashbal
                        </span>
                    </Link>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {pageState === 'form' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 border border-neutral-200 dark:border-neutral-800">
                            {/* Lock Icon */}
                            <div className="flex justify-center mb-8 h-8">
                                <h1 className="relative text-2xl font-semibold text-text-primary dark:text-neutral-100">
                                    <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap h-full">
                                        <Logo className="w-7 h-7" />
                                        <span className="w-4 h-4 text-neutral-300 dark:text-neutral-600" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_de24d260.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_de24d260.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                    </div>
                                    {t("auth.forgotPasswordTitle", "Forgot Password?")}
                                </h1>
                            </div>

                            <p className="text-neutral-600 dark:text-neutral-400 mb-6 text-center">
                                {t("auth.forgotPasswordDescription", "Enter your email address and we'll send you a link to reset your password.")}
                            </p>

                            {/* Error Message */}
                            {error && (
                                <div className="bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-800 rounded-lg p-3 mb-4">
                                    <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1">
                                        {t("auth.email", "Email")}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        required
                                        className="input w-full"
                                        placeholder={t("auth.emailPlaceholder", "Enter your email") as string}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="btn-primary w-full py-3"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center">
                                            <div className="spinner mr-2" />
                                            {t("actions.loading", "Loading...")}
                                        </span>
                                    ) : (
                                        t("auth.sendResetLink", "Send Reset Link")
                                    )}
                                </button>

                                <div className="text-center pt-2">
                                    <Link
                                        to="/?modal=login"
                                        className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-accent-primary dark:hover:text-accent-primary transition-colors"
                                    >
                                        ← {t("auth.backToSignIn", "Back to Sign In")}
                                    </Link>
                                </div>
                            </form>
                        </div>
                    )}

                    {pageState === 'success' && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                            {/* Email Icon */}
                            <div className="w-20 h-20 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <span className="w-10 h-10 text-success-600 dark:text-success-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_f1210338.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f1210338.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                            </div>

                            <h1 className="text-2xl font-semibold text-text-primary mb-2">
                                {t("auth.checkYourEmail", "Check Your Email")}
                            </h1>

                            <p className="text-neutral-600 dark:text-neutral-400 mb-2">
                                {t("auth.resetLinkSentTo", "We've sent a password reset link to:")}
                            </p>

                            <p className="font-medium text-text-primary mb-6">
                                {email}
                            </p>

                            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 mb-6 text-left">
                                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                    <strong className="text-text-primary">{t("auth.whatToDo", "What to do:")}</strong>
                                </p>
                                <ol className="text-sm text-neutral-600 dark:text-neutral-400 mt-2 space-y-2 list-decimal list-inside">
                                    <li>{t("auth.openEmailInbox", "Open your email inbox")}</li>
                                    <li>{t("auth.clickResetLink", "Click the password reset link in the email")}</li>
                                    <li>{t("auth.createNewPassword", "Create a new secure password")}</li>
                                </ol>
                            </div>

                            {/* Info Notice */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
                                <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                    <span className="w-4 h-4 flex-shrink-0" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_94839a80.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_94839a80.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                    <span>{t("auth.linkExpiresIn30", "The link will expire in 30 minutes for security.")}</span>
                                </p>
                            </div>

                            <Link
                                to="/?modal=login"
                                className="btn-secondary w-full py-3 inline-block text-center"
                            >
                                {t("auth.backToSignIn", "Back to Sign In")}
                            </Link>

                            {/* Help Text */}
                            <p className="text-xs text-neutral-400 mt-6">
                                {t("auth.cantFindEmail", "Can't find the email? Check your spam folder.")}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-neutral-200 dark:border-neutral-800 py-6">
                <div className="container-custom text-center">
                    <p className="text-sm text-neutral-500 dark:text-neutral-500">
                        © {new Date().getFullYear()} Dashbal. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default ForgotPasswordPage;
